import React, { useCallback, useEffect, type MouseEvent, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useYbrFeature } from '@jira/yellow-brick-road-utils/src/controllers/useYbrFeature.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type AiState,
	AIButtonPopup,
} from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-button-popup/index.tsx';
import { AIButton } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-button/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { cardKeys as spotlightCardKeys } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/spotlight-coordination/constants.tsx';
import { useSpotlightCoordinationActions } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/spotlight-coordination/index.tsx';
import SpotlightTourCard from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-card/index.tsx';
import SpotlightTourTarget, {
	type Props as SpotlightTourTargetProps,
} from '@atlassian/jira-issue-navigator-changeboarding/src/ui/spotlight-tour-target/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useJqlDebuggerStoreActions } from '@atlassian/jira-jql-debugger/src/controllers/jql-debugger-data/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIsClientReady } from '@atlassian/jira-ssr-toolkit/src/hooks/use-is-client-ready.tsx';

import { useIsAITourEnabled } from '../../controllers/ai-tour/index.tsx';
import type {
	NaturalLanguageToggle$key as NLToggleFragmentKey,
	NaturalLanguageToggle$data,
} from './__generated__/NaturalLanguageToggle.graphql';
import messages from './messages.tsx';

type StopFunction = () => void;
type CoordinationStopProviderProps = {
	stop?: StopFunction;
	children: (stop: StopFunction | undefined) => ReactElement;
};

function isToggleVisible(data: NaturalLanguageToggle$data | null): boolean {
	const action = data?.atlassianIntelligenceAction;

	if (
		!action ||
		action.__typename !== 'JiraAccessAtlassianIntelligenceFeature' ||
		!action.isAccessible
	) {
		return false;
	}

	return true;
}

function deriveAiState(data: NaturalLanguageToggle$data | null): AiState {
	const disabledAiState: AiState = {
		aiAvailable: false,
		aiEnabled: false,
	};
	const action = data?.atlassianIntelligenceAction;

	if (!action) {
		return disabledAiState;
	}

	// Case 1: AI is available, but disabled, and non org admins are notified
	if (action.__typename === 'JiraContactOrgAdminToEnableAtlassianIntelligence') {
		return {
			aiAvailable: true,
			aiEnabled: false,
			isOrgAdmin: false,
			orgAdminLink: null,
		};
	}

	// Case 2: AI is available, but disabled, and org admins are guided to the admin hub
	if (action?.__typename === 'JiraEnableAtlassianIntelligenceDeepLink' && action?.link) {
		return {
			aiAvailable: true,
			aiEnabled: false,
			isOrgAdmin: true,
			orgAdminLink: action.link,
		};
	}

	// Case 3: AI is available, and enabled
	if (action?.__typename === 'JiraAccessAtlassianIntelligenceFeature' && action.isAccessible) {
		return {
			aiAvailable: true,
			aiEnabled: true,
		};
	}

	// Default to AI unavailable
	return disabledAiState;
}

/**
 * Coordination client does some dirty injection of the `stop` prop in child components so we use this provider to make
 * the function accessible via a render prop.
 */
export const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

type Props = {
	autoFocus?: boolean;
	onClick: () => void;
	fragmentRef: NLToggleFragmentKey | null;
	onToggleBlur?: () => void;
};

const analyticsEventName = 'jqlBuilderAiToggle';

export const NaturalLanguageToggle = ({ autoFocus, onClick, fragmentRef, onToggleBlur }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { shouldShowYbrMessage } = useYbrFeature();
	const isClientReady = useIsClientReady();

	// Fires a button viewed analytic event on mount, which is used for the primary metric of the NL2JQL CTA experiment
	// https://home.atlassian.com/o/2346a038-3c8c-498b-a79b-e7847859868d/s/a436116f-02ce-4520-8fbb-7301462a1674/project/ATLAS-90055/updates
	useEffect(() => {
		if (fg('ai-event-for-nl2jql-cta-change')) {
			fireUIAnalytics(createAnalyticsEvent({}), 'aiExperience_nl2jql_button viewed');
		}
	}, [createAnalyticsEvent]);

	const jqlDebuggerStoreActions = useJqlDebuggerStoreActions();

	const [isAITourEnabled, { onDisableAITourUserPreference, onEndAITour }] = useIsAITourEnabled();
	const { onSetActiveCard, onEndTour } = useSpotlightCoordinationActions();

	const handleClick = useCallback(
		(_: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'button clicked', analyticsEventName, {
				aiAction: 'aiAccessible',
			});
			onClick();
		},
		[onClick],
	);

	const handleTourClick = useCallback(
		(analyticsEvent: UIAnalyticsEvent, stop?: StopFunction) => {
			// Prevent any user actions before the JQL builder becomes interactive
			fireUIAnalytics(analyticsEvent, 'button clicked', analyticsEventName, {
				aiAction: 'aiAccessible',
				isSpotlightTourViewed: true,
			});
			onDisableAITourUserPreference();
			onSetActiveCard(spotlightCardKeys.JQL_BUILDER_NL_TOGGLE, stop);
		},
		[onSetActiveCard, onDisableAITourUserPreference],
	);

	// Remove all the changeboarding on clean up of 'remove_nin_changeboarding_rll_usages', should be showing as empty fragments already
	const renderSpotlightCard: SpotlightTourTargetProps['renderSpotlightCard'] = (
		<SpotlightTourCard
			cardKey={spotlightCardKeys.JQL_BUILDER_NL_TOGGLE}
			actions={[
				{
					onClick: () => {
						onSetActiveCard(spotlightCardKeys.JQL_BUILDER_NL_TO_JQL);
						onClick();
					},
					text: formatMessage(messages.next),
				},
				{
					appearance: 'subtle',
					onClick: () => {
						// End the tour in AI tour store
						onEndAITour();
						// End the tour in NIN changeboarding store
						onEndTour();

						onDisableAITourUserPreference();
					},
					text: formatMessage(messages.close),
				},
			]}
			heading={formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.tourHeadingIssueTerminologyRefreshM4
					: messages.tourHeading,
			)}
			target="jql-builder-with-ai.natural-language-toggle"
			// On cleanup of nl-to-jql-cta-change (if the experiment is to be shipped), the border radius should be `4` to match the Atlaskit button
			targetRadius={
				expValEquals('nl-to-jql-cta-change', 'variant', 'FIND_WORK', 'CONTROL') ||
				expValEquals('nl-to-jql-cta-change', 'variant', 'QUICK_SEARCH', 'CONTROL') ||
				expValEquals('nl-to-jql-cta-change', 'variant', 'ASK_AI', 'CONTROL')
					? 4
					: 3
			}
			targetBgColor={token('elevation.surface')}
			testId="jql-builder-with-ai.ui.natural-language-toggle.spotlight"
			messageId="jql-builder-with-ai.ui.natural-language-toggle.spotlight-tour-card.jql-builder-with-ai.natural-language-toggle.spotlight"
			messageType="transactional"
		>
			{formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.tourBodyIssueTerminologyRefreshM4
					: messages.tourBody,
			)}
		</SpotlightTourCard>
	);

	const newNaturalLanguageToggleData = useFragment<NLToggleFragmentKey>(
		graphql`
			fragment NaturalLanguageToggle on JiraQuery {
				atlassianIntelligenceAction(
					cloudId: $cloudId
					atlassianIntelligenceProductFeatureInput: {
						feature: $atlassianIntelligenceProductFeature
					}
				) @optIn(to: "JiraNaturalLanguageToJQL") {
					... on JiraAccessAtlassianIntelligenceFeature {
						__typename
						isAccessible
					}
					... on JiraEnableAtlassianIntelligenceDeepLink {
						__typename
						link
					}
					... on JiraContactOrgAdminToEnableAtlassianIntelligence {
						__typename
					}
				}
			}
		`,
		fragmentRef,
	);

	// Spotlight tour should be disabled when AI is not enabled for the org since we still show the AI button with the appropriate popup
	const aiState = deriveAiState(newNaturalLanguageToggleData ?? null);

	const aiToggleVisible = fg('jira_ai_nl2jql_disable_admin_popup')
		? isToggleVisible(newNaturalLanguageToggleData ?? null)
		: aiState.aiAvailable && aiState.aiEnabled;

	// JQL debugger inherits the AI enabled state from NL-to-JQL as that is the parent experience
	// i.e. At this stage the JQL debugger only exists in the context of NL-to-JQL in Issue Navigator
	jqlDebuggerStoreActions?.setIsAiEnabled(aiToggleVisible);

	if (
		shouldShowYbrMessage({
			shouldDisplayMessage: isAITourEnabled && aiToggleVisible,
		}) &&
		(fg('jfp-magma-nin-hydration-fixes') ? isClientReady : true)
	) {
		return (
			<SpotlightPulse
				// On cleanup of nl-to-jql-cta-change (if the experiment is to be shipped), the border radius should be `4` to match the Atlaskit button
				radius={
					expValEquals('nl-to-jql-cta-change', 'variant', 'FIND_WORK', 'CONTROL') ||
					expValEquals('nl-to-jql-cta-change', 'variant', 'QUICK_SEARCH', 'CONTROL') ||
					expValEquals('nl-to-jql-cta-change', 'variant', 'ASK_AI', 'CONTROL')
						? 4
						: 8
				}
			>
				<SpotlightTourTarget
					name="jql-builder-with-ai.natural-language-toggle"
					renderSpotlightCard={renderSpotlightCard}
				>
					{fg('jira_ai_nl2jql_disable_admin_popup') ? (
						aiToggleVisible && (
							<AIButton
								onClick={(_, analyticsEvent) => handleTourClick(analyticsEvent)}
								autoFocus={autoFocus}
								onToggleBlur={onToggleBlur}
							/>
						)
					) : (
						<AIButtonPopup
							aiState={aiState}
							autoFocus={autoFocus}
							onClick={(_, analyticsEvent) => handleTourClick(analyticsEvent)}
							analyticsSource="jiraNaturalLanguageToJql"
							analyticsEventName={analyticsEventName}
							onToggleBlur={onToggleBlur}
						/>
					)}
				</SpotlightTourTarget>
			</SpotlightPulse>
		);
	}

	return fg('jira_ai_nl2jql_disable_admin_popup') ? (
		aiToggleVisible && (
			<AIButton onClick={handleClick} autoFocus={autoFocus} onToggleBlur={onToggleBlur} />
		)
	) : (
		<AIButtonPopup
			aiState={aiState}
			autoFocus={autoFocus}
			onClick={handleClick}
			analyticsSource="jiraNaturalLanguageToJql"
			analyticsEventName={analyticsEventName}
			onToggleBlur={onToggleBlur}
		/>
	);
};
